import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  FormField,
  Input,
  Modal,
  SpaceBetween,
} from "@amzn/awsui-components-react/polaris";

/**
 * "Adhoc task manager" dialog (admin only).
 *
 * Collects an arbitrary list of free-text statements. Each entry is a full
 * sentence typed into its own input; "Add" appends another input, and the ×
 * removes a row. No fixed limit.
 *
 * Backendless for now — Save just hands the non-empty statements back.
 */
const AdhocTaskManagerModal = ({ visible, onDismiss, onSave }) => {
  // Each row carries a stable id so React keys follow the data (not the array
  // index). Keying on the index makes controlled <Input>s keep the focus/cursor
  // of whatever row previously sat at that index when a middle row is removed.
  const nextId = useRef(0);
  const makeRow = (value = "") => ({ id: nextId.current++, value });
  // Start with a single empty input row.
  const [statements, setStatements] = useState(() => [makeRow()]);

  useEffect(() => {
    if (visible) setStatements([makeRow()]);
    // makeRow is stable for the component's lifetime; only re-seed on open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  const updateAt = (id, value) =>
    setStatements((prev) => prev.map((s) => (s.id === id ? { ...s, value } : s)));

  const addRow = () => setStatements((prev) => [...prev, makeRow()]);

  const removeAt = (id) =>
    setStatements((prev) => (prev.length > 1 ? prev.filter((s) => s.id !== id) : [makeRow()]));

  const handleSave = () => {
    const cleaned = statements.map((s) => s.value.trim()).filter(Boolean);
    onSave?.(cleaned);
  };

  return (
    <Modal
      visible={visible}
      onDismiss={onDismiss}
      header="Adhoc task manager"
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={onDismiss}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleSave}>
              Save
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <FormField
        label="Statements"
        description="Type a statement per line. Use Add to enter as many as you want."
      >
        <SpaceBetween size="xs">
          {statements.map(({ id, value }, index) => (
            <div
              key={id}
              style={{ display: "flex", alignItems: "center", gap: 8 }}
            >
              <div style={{ flex: 1 }}>
                <Input
                  placeholder="Type a statement"
                  value={value}
                  onChange={({ detail }) => updateAt(id, detail.value)}
                />
              </div>
              <Button
                variant="icon"
                iconName="close"
                ariaLabel={`Remove statement ${index + 1}`}
                onClick={() => removeAt(id)}
              />
            </div>
          ))}
          <Box>
            <Button iconName="add-plus" onClick={addRow}>
              Add
            </Button>
          </Box>
        </SpaceBetween>
      </FormField>
    </Modal>
  );
};

export default AdhocTaskManagerModal;
